import { useCallback, useEffect, useRef } from 'react';

import { useStore } from '../../store';
import { initialPreviewedDocument } from '../../store/states/ui';
import { DatatableSidebarLoader } from '../table/sidebar/DatatableSidebarLoader';
import { DocviewSidebarLoader } from '../table/sidebar/DocviewSidebarLoader';
import { isSameTableSidebarRow } from '../table/sidebar/isActiveSidebarRowInTableItems';

import {
  PageSidebarActiveContent,
  type PageSidebarPreviewedDocument,
  PageSidebarScope,
} from './types';

export type PageSidebarOutletProps = {
  active: PageSidebarActiveContent;
  opened: boolean;
  scope?: PageSidebarScope;
};

export function PageSidebarOutlet({
  active,
  opened,
  scope = 'page',
}: PageSidebarOutletProps) {
  const setPreviewedDocument = useStore(
    (state) => state.setPageSidebarPreviewedDocument
  );
  const previousActiveRef = useRef<PageSidebarActiveContent | undefined>(
    undefined
  );

  // Rows without a business id keep their sidebar mounted across a target
  // change, so the document panel can still report the previous row's
  // document. Reset to the new row's document until it reports again.
  useEffect(() => {
    const previousActive = previousActiveRef.current;

    if (previousActive === undefined) {
      previousActiveRef.current = active;
      return;
    }

    const isSameSidebarTarget =
      previousActive.widgetId === active.widgetId &&
      previousActive.sideBarType === active.sideBarType &&
      isSameTableSidebarRow(previousActive.data, active.data);

    previousActiveRef.current = active;

    if (isSameSidebarTarget) {
      return;
    }

    setPreviewedDocument(scope, initialPreviewedDocument(active));
  }, [active, scope, setPreviewedDocument]);

  const handleSelectedDocumentChange = useCallback(
    (document: PageSidebarPreviewedDocument | null) => {
      setPreviewedDocument(scope, document);
    },
    [scope, setPreviewedDocument]
  );

  if (active.sideBarType === 'docview') {
    return (
      <DocviewSidebarLoader
        data={active.data}
        fileDownloadColumnKeys={active.fileDownloadColumnKeys}
        onSelectedDocumentChange={handleSelectedDocumentChange}
        showWorkflowFiles={active.showWorkflowFiles}
        widgetId={active.widgetId}
      />
    );
  }

  return (
    <DatatableSidebarLoader
      data={active.data}
      fileDownloadColumnKeys={active.fileDownloadColumnKeys}
      onSelectedDocumentChange={handleSelectedDocumentChange}
      opened={opened}
      relatedDocumentsConfig={active.relatedDocumentsConfig}
      showWorkflowFiles={active.showWorkflowFiles}
      widgetId={active.widgetId}
    />
  );
}
